export interface Storage {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

/** 平台存储键名集中定义 */
export const STORAGE_KEYS = {
  boardHistory: 'games.fog-chess.board-history',
  roomTokenPrefix: 'games.room-token.',
} as const

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function createMemoryStorage(): Storage {
  const entries = new Map<string, string>()
  return {
    get: (key) => entries.get(key) ?? null,
    set: (key, value) => {
      entries.set(key, value)
    },
    remove: (key) => {
      entries.delete(key)
    },
  }
}

/** 隐私模式/沙箱下 localStorage 的读写都可能抛异常，所以连探测也要包在 try 里 */
function probeBrowserStorage(name: 'localStorage' | 'sessionStorage'): StorageLike | null {
  try {
    const candidate = (globalThis as unknown as Record<string, StorageLike | undefined>)[name]
    if (!candidate) return null
    const probeKey = '__games-storage-probe__'
    candidate.setItem(probeKey, '1')
    candidate.removeItem(probeKey)
    return candidate
  } catch {
    return null
  }
}

const memory = createMemoryStorage()
let browser: StorageLike | null | undefined

function backend(): StorageLike | null {
  if (browser === undefined) browser = probeBrowserStorage('localStorage')
  return browser
}

/** 浏览器实现：localStorage，失败（隐私模式、配额）时降级为内存实现 */
export const storage: Storage = {
  get: (key) => {
    const target = backend()
    if (target) {
      try {
        return target.getItem(key)
      } catch {
        // 读取被拒绝时同样降级，不让调用方看到异常
      }
    }
    return memory.get(key)
  },
  set: (key, value) => {
    const target = backend()
    if (target) {
      try {
        target.setItem(key, value)
        return
      } catch {
        // 写入失败（如配额用尽）时降级到内存
      }
    }
    memory.set(key, value)
  },
  remove: (key) => {
    const target = backend()
    if (target) {
      try {
        target.removeItem(key)
        return
      } catch {
        // 与 get/set 保持一致：失败就降级
      }
    }
    memory.remove(key)
  },
}

/**
 * 会话级存储：刷新还在、关掉标签页就没了。
 *
 * 房间身份令牌用它（不用 localStorage）：令牌只在「这个标签页的这一局」里有意义，
 * 关掉标签页再进来就是一个新人，不该把旧座位带回来。
 */
const sessionMemory = createMemoryStorage()
let sessionBrowser: StorageLike | null | undefined

function sessionBackend(): StorageLike | null {
  if (sessionBrowser === undefined) sessionBrowser = probeBrowserStorage('sessionStorage')
  return sessionBrowser
}

export const sessionStorageLike: Storage = {
  get: (key) => {
    const target = sessionBackend()
    if (target) {
      try {
        return target.getItem(key)
      } catch {
        // 忽略：降级到内存
      }
    }
    return sessionMemory.get(key)
  },
  set: (key, value) => {
    const target = sessionBackend()
    if (target) {
      try {
        target.setItem(key, value)
        return
      } catch {
        // 忽略：降级到内存
      }
    }
    sessionMemory.set(key, value)
  },
  remove: (key) => {
    const target = sessionBackend()
    if (target) {
      try {
        target.removeItem(key)
        return
      } catch {
        // 忽略：降级到内存
      }
    }
    sessionMemory.remove(key)
  },
}

/** 房间身份令牌的存储键：按房间号隔离，不同房间各留各的 */
export function roomTokenKey(roomCode: string): string {
  return `${STORAGE_KEYS.roomTokenPrefix}${roomCode}`
}

/** 读取本标签页在该房间的身份令牌；没有就返回 null（= 新人） */
export function getRoomToken(roomCode: string): string | null {
  return sessionStorageLike.get(roomTokenKey(roomCode))
}

let tokenCounter = 0

/**
 * 生成房间身份令牌。**禁止 Math.random**（见 AGENTS.md），只走 `crypto`；
 * 没有 `crypto.randomUUID`（非安全上下文，例如局域网 http 调试）时退化为
 * 时间戳 + 自增计数：唯一性足够，只是不可猜性下降。
 */
export function createRoomToken(roomCode: string): string {
  const token = randomToken()
  sessionStorageLike.set(roomTokenKey(roomCode), token)
  return token
}

function randomToken(): string {
  const cryptoApi = (globalThis as { crypto?: Crypto }).crypto
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID()
  tokenCounter += 1
  return `token-${Date.now().toString(36)}-${tokenCounter.toString(36)}`
}
